import * as THREE from 'three'

// 导入 shader
import gsplatResolveSHFrag from '@/plugins/gsplat/shaders/frag/gsplatResolveSH.fs'
import { registerGSplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import { GSPLAT_SH_DIRECTION_THRESHOLD } from '@/shared/constants'

/**
 * SH 更新模式
 */
export type SHUpdateMode = 'enable' | 'disable' | 'always'

/**
 * 高质量 SH 解析器
 *
 * 使用 GPU 预计算 SH 球谐函数值，将结果存储在查找纹理中
 * 渲染时通过 sh_labels 查找预计算的 SH 颜色，避免每顶点计算
 *
 * 参考 PlayCanvas gsplat-resolve-sh.js
 *
 * 工作原理：
 * 1. 每帧检测相机方向变化
 * 2. 如果方向变化超过阈值，重新渲染 SH 查找纹理
 * 3. 渲染时使用 sh_result 纹理查找预计算的颜色
 *
 * 性能提升：10-30%（避免每顶点 SH 评估）
 */
export class GSplatResolveSH {
  private renderer: THREE.WebGLRenderer
  private shBands: number

  /** SH 结果纹理 (64x1024 RGBA8) - 直接使用 RenderTarget 的纹理 */
  readonly texture: THREE.Texture

  /** 渲染目标 */
  private renderTarget: THREE.WebGLRenderTarget

  /** 全屏四边形场景 */
  private quadScene: THREE.Scene
  private quadCamera: THREE.OrthographicCamera
  private quadMesh: THREE.Mesh
  private processedFragment: string

  /** 上一帧的视角方向 */
  private prevDir: THREE.Vector3 = new THREE.Vector3()

  /** 更新模式 */
  updateMode: SHUpdateMode = 'enable'

  /** 方向变化阈值 */
  private directionThreshold: number = GSPLAT_SH_DIRECTION_THRESHOLD

  /** SH centroids 纹理引用 */
  private centroidsTexture: THREE.Texture | null = null

  /** 渲染计数器（调试用） */
  private renderCount = 0

  constructor(renderer: THREE.WebGLRenderer, shBands: number) {
    this.renderer = renderer
    this.shBands = shBands
    this.processedFragment = registerGSplatChunks().assembleSource(gsplatResolveSHFrag)

    // 创建 SH 结果纹理 (64x1024 RGBA8)
    // 最多支持 64*1024 = 65536 个调色板条目
    const width = 64
    const height = 1024

    // 修复：先创建 RenderTarget，直接使用其纹理
    // 避免独立 DataTexture + CPU 回读的错误架构
    this.renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: false,
      stencilBuffer: false,
    })

    // 直接使用 RenderTarget 的纹理，无需单独创建 DataTexture
    this.texture = this.renderTarget.texture
    this.texture.name = 'shResultTexture'

    // 创建全屏四边形
    this.quadScene = new THREE.Scene()
    this.quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

    const geometry = new THREE.BufferGeometry()
    const vertices = new Float32Array([-1, -1, 3, -1, -1, 3])
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 2))

    // 创建材质（稍后设置 centroids 纹理）
    const material = this.createMaterial()
    this.quadMesh = new THREE.Mesh(geometry, material)
    this.quadScene.add(this.quadMesh)
  }

  /**
   * 创建 SH 解析材质
   */
  private createMaterial(): THREE.RawShaderMaterial {
    return new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: /* glsl */ `
        in vec2 position;
        void main() {
          gl_Position = vec4(position, 0.0, 1.0);
        }
      `,
      fragmentShader: this.processedFragment,
      uniforms: {
        dir: { value: new THREE.Vector3(0, 0, 1) },
        centroids: { value: null },
        shN_mins: { value: 0.0 },
        shN_maxs: { value: 1.0 },
      },
      defines: {
        SH_BANDS: String(this.shBands),
      },
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
    })
  }

  /**
   * 设置 SH centroids 纹理及其反量化范围。
   *
   * @param texture - SH palette 纹理
   * @param mins - 反量化下界
   * @param maxs - 反量化上界
   */
  setCentroids(texture: THREE.Texture, mins: number, maxs: number): void {
    this.centroidsTexture = texture

    const material = this.quadMesh.material as THREE.RawShaderMaterial
    material.uniforms.centroids.value = texture
    material.uniforms.shN_mins.value = mins
    material.uniforms.shN_maxs.value = maxs
  }

  /**
   * 渲染 SH 查找纹理
   *
   * @param camera 相机
   * @param modelMatrix 模型矩阵
   * @returns 是否进行了渲染
   */
  render(camera: THREE.Camera, modelMatrix: THREE.Matrix4): boolean {
    // 禁用模式
    if (this.updateMode === 'disable') {
      return false
    }

    // 检查 centroids 纹理是否设置
    if (!this.centroidsTexture) {
      return false
    }

    // 计算模型空间中的相机 Z 方向
    const invModelMat = modelMatrix.clone().invert()

    // 获取相机前向方向（世界空间）
    const cameraForward = new THREE.Vector3(0, 0, -1)
    cameraForward.applyQuaternion(camera.quaternion)

    // 转换到模型空间
    const dir = cameraForward.clone().transformDirection(invModelMat).normalize()

    // 如果方向没有变化（且不是 always 模式），提前退出
    if (this.updateMode === 'enable') {
      const diff = dir.distanceTo(this.prevDir)
      if (diff < this.directionThreshold) {
        return false
      }
    }

    // 保存当前方向
    this.prevDir.copy(dir)

    // 更新 uniform
    const material = this.quadMesh.material as THREE.RawShaderMaterial
    material.uniforms.dir.value.copy(dir)

    // 执行渲染
    const oldRenderTarget = this.renderer.getRenderTarget()
    this.renderer.setRenderTarget(this.renderTarget)
    this.renderer.render(this.quadScene, this.quadCamera)
    this.renderer.setRenderTarget(oldRenderTarget)

    this.renderCount++

    // 修复：移除 CPU 回读代码
    // 现在 this.texture 直接指向 renderTarget.texture
    // GPU 渲染结果可以直接在 shader 中使用，无需 CPU 介入

    return true
  }

  /**
   * 获取用于 shader 的 uniforms。
   *
   * @returns 仅包含 sh_result 采样器的 uniform 字典
   */
  getUniforms(): Record<string, THREE.IUniform> {
    return {
      sh_result: { value: this.texture },
    }
  }

  /**
   * 释放资源
   */
  dispose(): void {
    this.texture.dispose()
    this.renderTarget.dispose()
    ;(this.quadMesh.material as THREE.Material).dispose()
    this.quadMesh.geometry.dispose()
  }
}

/**
 * 用于 SOGS 数据的高质量 SH 读取颜色函数（GLSL chunk）
 *
 * 当启用高质量 SH 时，使用此 chunk 替换默认的 readColor
 *
 * 注意：使用与 SHBitPacker.shBitPackingChunk 相同的解包逻辑
 * 确保打包/解包一致性
 */
export const gsplatSogsHighQualitySHChunk = /* glsl */ `
// 高质量 SH 解析 uniforms
uniform sampler2D sh_result;

const float SH_C0 = 0.28209479177387814;

// 解包 11+11+10 位打包的 RGB 值（与 SHBitPacker.shBitPackingChunk 一致）
vec3 unpackSHRgb(vec4 v) {
  uvec4 uv = uvec4(v * 255.0);
  uint bits = (uv.x << 24u) | (uv.y << 16u) | (uv.z << 8u) | uv.w;
  uvec3 vb = (uvec3(bits) >> uvec3(21u, 10u, 0u)) & uvec3(0x7ffu, 0x7ffu, 0x3ffu);
  return vec3(vb) / vec3(2047.0, 2047.0, 1023.0);
}

// 反归一化 SH 值（与 SHBitPacker.shBitPackingChunk 一致）
vec3 denormalizeSH(vec3 normalized) {
  return (normalized - 0.5) * 4.0;
}

// 高质量 SH 颜色读取（使用预计算的 SH 查找纹理）
vec4 readColorHighQualitySH(in SplatSource source) {
  // 采样基础颜色
  vec4 baseSample = mix(sh0Mins, sh0Maxs, texelFetch(sh0, source.uv, 0));

  // 解析基础颜色
  vec4 base = vec4(vec3(0.5) + baseSample.xyz * SH_C0, 1.0 / (1.0 + exp(-baseSample.w)));

  // 提取球谐调色板索引
  ivec2 labelSample = ivec2(texelFetch(sh_labels, source.uv, 0).xy * 255.0);
  int n = labelSample.x + labelSample.y * 256;

  // 从预计算的 SH 结果纹理查找
  vec4 shSample = texelFetch(sh_result, ivec2(n % 64, n / 64), 0);
  vec3 sh = denormalizeSH(unpackSHRgb(shSample));

  return vec4(base.xyz + sh, base.w);
}
`
